import React, { useState, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Citation, CitationDescription } from '../../types/citation';
import { parseCitationsSync } from '../../utils/citationParser';
import CitationTooltip from './CitationTooltip';
import CitationModal from './CitationModal';

interface CitationRendererProps {
  content: string;
  citationDescriptions?: { [citationId: number]: CitationDescription | string }; // Support both old and new formats
  citationMetadata?: { [key: string]: any }; // Raw citation metadata from backend
  className?: string;
}

const CitationRenderer: React.FC<CitationRendererProps> = ({ 
  content, 
  citationDescriptions, 
  citationMetadata,
  className 
}) => {
  const [hoveredCitation, setHoveredCitation] = useState<Citation | null>(null);
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | undefined>();

  // Parse citations and process content
  const { citations, processedContent } = useMemo(() => 
    parseCitationsSync(content, citationDescriptions, citationMetadata), 
    [content, citationDescriptions, citationMetadata]
  );

  const handleCitationHover = useCallback((citation: Citation | null, event?: React.MouseEvent | React.PointerEvent) => {
    setHoveredCitation(citation);
    if (citation && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    } else {
      setTooltipPosition(undefined);
    }
  }, []);

  const handleCitationClick = useCallback((citation: Citation) => {
    setSelectedCitation(citation);
    setModalOpen(true);
    setHoveredCitation(null); // Hide tooltip when modal opens
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedCitation(null);
  }, []);

  // Convert citation placeholders to a special format that we can handle with custom components
  const processedMarkdown = useMemo(() => {
    if (citations.length === 0) {
      return content;
    }

    // Replace citation placeholders with markdown that we can intercept
    let result = processedContent;
    citations.forEach(citation => {
      const placeholder = `__CITATION_${citation.id}__`;
      // Use a format that markdown won't interfere with but we can detect
      result = result.replace(placeholder, `[CITE:${citation.id}]`);
    });

    console.log('Processed markdown:', result); // Debug log
    console.log('Citations found:', citations); // Debug log

    return result;
  }, [citations, processedContent, content]);

  // Custom component to handle any element that might contain citation text
  const ProcessTextContent = ({ children }: { children: React.ReactNode }) => {
    const processTextNode = (node: React.ReactNode): React.ReactNode => {
      if (typeof node === 'string') {
        const citationRegex = /\[CITE:(\d+)\]/g;
        
        // Check if string contains citations
        if (!citationRegex.test(node)) {
          return node;
        }

        // console.log('Processing text with citations:', node); // Debug log

        // Reset regex for splitting
        const splitRegex = /\[CITE:(\d+)\]/g;
        const parts = node.split(splitRegex);
        const elements: React.ReactNode[] = [];

        for (let i = 0; i < parts.length; i++) {
          if (i % 2 === 0) {
            // Regular text
            if (parts[i]) {
              elements.push(parts[i]);
            }
          } else {
            // Citation ID
            const citationId = parseInt(parts[i], 10);
            const citation = citations.find(c => c.id === citationId);
            
            console.log('Found citation ID:', citationId, 'Citation:', citation); // Debug log
            
            if (citation) {
              elements.push(
                <span
                  key={`citation-${citationId}-${i}`}
                  className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded cursor-pointer transition-colors duration-150 ml-0.5 hover-glow-sm"
                  style={{ verticalAlign: 'super', fontSize: '0.7em' }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Citation ${citation.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCitationClick(citation);
                  }}
                  onPointerEnter={(e) => handleCitationHover(citation, e)}
                  onPointerLeave={() => handleCitationHover(null)}
                >
                  {citation.id}
                </span>
              );
            }
          }
        }

        return <>{elements}</>;
      }

      if (React.isValidElement(node) && node.props.children) {
        return React.cloneElement(node, {
          ...node.props,
          children: React.Children.map(node.props.children, processTextNode)
        });
      }

      return node;
    };

    return <>{React.Children.map(children, processTextNode)}</>;
  };

  return (
    <div className="relative">
      <ReactMarkdown 
        className={className}
        remarkPlugins={[remarkGfm]}
        components={{
          h1: "h2",
          h2: "h3",
          // Override all text-containing elements to process citations
          p: ({ children }) => <ProcessTextContent><p>{children}</p></ProcessTextContent>,
          strong: ({ children, ...props }) => <ProcessTextContent><strong {...props}>{children}</strong></ProcessTextContent>,
          // em: ({ children, ...props }) => <ProcessTextContent><em {...props}>{children}</em></ProcessTextContent>,
          // Use any to bypass strict typing for now
          li: (({ children, ...props }: any) => <ProcessTextContent><li {...props}>{children}</li></ProcessTextContent>) as any,
          td: (({ children, ...props }: any) => <ProcessTextContent><td {...props}>{children}</td></ProcessTextContent>) as any,
          th: (({ children, ...props }: any) => <ProcessTextContent><th {...props}>{children}</th></ProcessTextContent>) as any,
        }}
      >
        {processedMarkdown}
      </ReactMarkdown>
      
      {/* Tooltip */}
      <CitationTooltip 
        citation={hoveredCitation} 
        position={tooltipPosition}
      />
      
      {/* Modal */}
      <CitationModal
        citation={selectedCitation}
        isOpen={modalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
};

export default CitationRenderer;
